<template>
  <label class="signup-tf" :class="{ 'signup-tf--error': !!error, 'signup-tf--filled': !!value }">
    <span v-if="label" class="signup-tf__label">
      {{ label }}
      <span v-if="required" class="signup-tf__req" aria-hidden="true">*</span>
    </span>
    <input
      ref="input"
      class="signup-tf__input"
      :type="type"
      :value="value"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :maxlength="maxlength || undefined"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    />
    <span v-if="error" class="signup-tf__error" role="alert">{{ error }}</span>
  </label>
</template>

<script>
/**
 * SignupTextField — uppercase floating label + 48px rounded input.
 * Used across B2 signup wizard steps. Standalone (no external deps).
 *
 * Props:
 *  - label: String           — small-caps label above the input
 *  - value: String           — v-model value
 *  - placeholder: String
 *  - type: String            — 'text' | 'email' | etc. (default 'text')
 *  - required: Boolean       — shows an asterisk in the label
 *  - disabled: Boolean
 *  - error: String           — inline error text below the input
 *  - autocomplete: String    — native autocomplete hint
 *  - maxlength: Number
 *
 * Emits:
 *  - input (value)    — v-model update
 *  - blur              — focus left
 *  - focus             — focus entered
 */
export default {
  name: 'SignupTextField',
  props: {
    label: { type: String, default: '' },
    value: { type: [String, Number], default: '' },
    placeholder: { type: String, default: '' },
    type: { type: String, default: 'text' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    autocomplete: { type: String, default: 'off' },
    maxlength: { type: Number, default: 0 },
  },
  methods: {
    onInput(e) {
      this.$emit('input', e.target.value)
    },
    onBlur(e) {
      this.$emit('blur', e)
    },
    onFocus(e) {
      this.$emit('focus', e)
    },
    focus() {
      if (this.$refs.input) this.$refs.input.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../_shared.scss';

.signup-tf {
  display: block;
  margin-bottom: 18px;

  &:last-child { margin-bottom: 0; }
}

.signup-tf__label {
  display: block;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--signup-text-muted);
  margin-bottom: 8px;
}

.signup-tf__req {
  color: var(--signup-brand);
  margin-left: 2px;
  font-weight: 900;
}

.signup-tf__input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  padding: 0 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: var(--signup-text);
  background: var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;

  &::placeholder {
    color: #a0a0b5;
    font-weight: 400;
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: #d5cde8;
  }

  &:focus {
    outline: none;
    border-color: var(--signup-brand);
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.15);
  }

  &:disabled {
    background: var(--signup-surface-subtle);
    color: var(--signup-text-muted);
    cursor: not-allowed;
  }
}

.signup-tf--error .signup-tf__input {
  border-color: var(--signup-danger);

  &:focus {
    box-shadow: 0 0 0 3px rgba(204, 48, 48, 0.15);
  }
}

.signup-tf__error {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--signup-danger);
  line-height: 1.4;
}
</style>
